import { useNavigate } from 'react-router-dom';
import { useFreeTier } from '@/access/useFreeTier';
import { useCanvasActions, useCanvases } from '@/library/useCanvases';
import { useBookmarkCount } from '@/library/useBookmarks';
import { useT } from '@/i18n/LocaleProvider';
import { BoardIcon, BookmarkIcon } from '@/components/icons/NavIcons';
import { cx } from '@/lib/cx';
import { CanvasesView } from './CanvasesView';
import styles from './Library.module.css';
import { SavedView } from './SavedView';

export type LibraryTab = 'saved' | 'canvases';

/** Библиотека (README §4): рельса 220px с «+ New canvas» и разделами Saved / Canvases; на планшете — сегменты. */
export function LibraryPage({ tab }: { tab: LibraryTab }) {
  const t = useT();
  const navigate = useNavigate();
  const bookmarkCount = useBookmarkCount();
  const { data: canvases = [] } = useCanvases();
  const { createCanvas } = useCanvasActions();

  const { guardCreateCanvas } = useFreeTier();

  const newCanvas = async () => {
    // Бесплатный тариф: один канвас — по клику окно оплаты, кнопка не блокируется.
    if (!guardCreateCanvas()) return;
    const created = await createCanvas('library');
    navigate(`/canvases/${created.id}`);
  };

  const tabs: Array<{
    key: LibraryTab;
    label: string;
    count: number;
    path: string;
    dashed?: boolean;
  }> = [
    { key: 'saved', label: t('nav.saved'), count: bookmarkCount, path: '/saved' },
    {
      key: 'canvases',
      label: t('nav.canvases'),
      count: canvases.length,
      path: '/canvases',
      dashed: true,
    },
  ];

  return (
    <div className={styles.layout}>
      <aside className={styles.rail} aria-label={t('library.eyebrow')}>
        <button
          type="button"
          className={styles.railNew}
          data-tour="canvas-new"
          onClick={() => void newCanvas()}
        >
          {t('library.newCanvas')}
        </button>
        {tabs.map((item) => (
          <button
            key={item.key}
            type="button"
            className={cx(styles.railTab, tab === item.key && styles.railTabActive)}
            aria-current={tab === item.key ? 'page' : undefined}
            onClick={() => navigate(item.path)}
          >
            {item.dashed ? (
              <BoardIcon className={styles.railIcon} />
            ) : (
              <BookmarkIcon className={styles.railIcon} />
            )}
            <span className={styles.railLabel}>{item.label}</span>
            <span className={styles.railCount}>{item.count}</span>
          </button>
        ))}
      </aside>

      <div className={styles.page}>
        <div className={styles.tabs} role="tablist" aria-label={t('library.eyebrow')}>
          {tabs.map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={tab === item.key}
              className={cx(styles.tab, tab === item.key && styles.tabActive)}
              onClick={() => navigate(item.path)}
            >
              {item.label} · {item.count}
            </button>
          ))}
        </div>
        {tab === 'saved' ? <SavedView /> : <CanvasesView onNewCanvas={() => void newCanvas()} />}
      </div>
    </div>
  );
}
